import {
  IInventoryCatalogueImage,
  IInventoryCataloguePropsBody,
  IInventoryCreateDropdownListProps,
} from "@/api_framework/api_modals/Inventory";
import Input from "@/app/components/ui/Inputs/Input";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import ToggleWithIcon from "@/app/components/ui/Toggles/ToggleWithIcon";
import { usePOUnitAPI } from "@/app/hooks/api_hooks/Inventory/PO/usePOUnitAPI";
import { useInventoryCountryListAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryCountryListAPI";
import { useInventoryProductCatalogueDropdownItemCreateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCatalogueDropdownItemCreateAPI";
import { useInventoryProductCatalogueUpdateAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCatalogueUpdateAPI";
import { useInventoryProductCatalougeDropdownAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCatalougeDropdownAPI";
import { useInventoryProductCategoryDropdownAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductCategoryDropdownAPI";
import { useInventoryProductDetailsAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryProductDetailsAPI";
import useFormValidations from "@/app/hooks/useFormValidations";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { useRouter } from "@/app/hooks/useRouter";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { useEffect, useState } from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { toast } from "react-hot-toast";
import { IoMdArrowRoundDown } from "react-icons/io";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import CopyFromOtherProduct from "./CopyFromOtherProduct";
import { FiPlusCircle } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { usePackSellEligibilityAPI } from "@/app/hooks/api_hooks/Inventory/ProductSetup/usePackSellEligibilityAPI";
import InputWithFileUpload from "@/app/components/ui/Inputs/InputWithFileUpload";
import { useModalActions } from "@/app/hooks/ui_hooks/useModalActions";
import ModalPrompt from "@/app/components/ui/Modals/Prompt";
import { TrashIcon } from "@heroicons/react/24/outline";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";
import clsx from "clsx";
import { useInventoryManufacturereListActiveAPI } from "@/app/hooks/api_hooks/Inventory/useInventoryManufacturereListActiveAPI";
import { useFileToS3LinkGeneratorAPI } from "@/app/hooks/api_hooks/LabReport/useFileToS3LinkGeneratorAPI";
import { IInventoryFormFields } from "../GeneralDetailsFormComponent";
import { useBreedListAPI } from "@/app/hooks/api_hooks/Inventory/ProductSetup/useBreedListAPI";
import { Loader } from "lucide-react";
import ToggleableRadioGroup from "../ToggleRadioGroup";
import CreatableSelect from "react-select/creatable";
import { PRIMARY_BUTTON } from "@/constants";
import { useCouponSubCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponSubCategoryAPI";

export interface IPack {
  pack: number | string;
  discount: number;
  highlight: boolean;
  visible: boolean;
}

export interface IInventoryProductCatalogueFormFields {
  itemName: string;
  productDescription: string;
  unitType: string;
  productWeight: number;
  flavour: string;
  productForm: string;
  breadSpecific: string;
  breadSize: string;
  category: string;
  categoryId: string;
  subCategory: string;
  subCategoryId: string;
  subSubCategory: string;
  subSubCategoryId: string;
  appLevelDiscount: number;
  appVisible: boolean;
  unitCount: string;
  countryOrigin: string;
  variationTheme: string;
  manufacturerContactInformation: string;
  importerContactInformation: string;
  packageWeight: number;
  highlight: boolean;
  visible: boolean;
  qc_catalogue: boolean;
  age_range: string;
  external_product: string;
  weightUnit: string;
  packsNumber: boolean;
  packs: IPack[];
  is_pack_sell_eligible: boolean;
  image: IInventoryCatalogueImage[];
  catalogue_images_files: FileList;
  productLength: number;
  productWidth: number;
  productHeight: number;
  manufacturer: string;
  manufacturerId: string;
  pet_breed_id: number;
  medicine_tags: Array<string>;
  composition: string;
}

interface Props {
  formHook: UseFormReturn<IInventoryFormFields>;
}

const CatalogDetailsFormComponent = ({ formHook }: Props) => {
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { unitList, productCopyDetails, countryList, manufacturerList, breedSpecificList } =
    routeState.state as IInventoryState;
  const { forNumericWithDecimal, forTaxRegex, forAllAcceptWithOutStartWithDotAndSpace, forOnlyNumber } =
    useFormValidations();
  const [params, _setparams] = useSearchParams();
  const [copyModal, setCopyModal] = useState(false);
  const { searchResults: flavourItems, execute: fetchFlavour } = useInventoryProductCatalougeDropdownAPI("FLAVOUR");
  const { searchResults: categoryItems, execute: fetchCategory } = useInventoryProductCategoryDropdownAPI("categories");
  const { searchResults: subCategoryItems, execute: fetchSubCategory } =
    useInventoryProductCategoryDropdownAPI("sub-categories");
  const { searchResults: subSubcategoryItems, execute: fetchSubsubCategory } =
    useInventoryProductCategoryDropdownAPI("sub-subcategories");
  const { searchResults: breedSizeItems, execute: fetchBreedSize } =
    useInventoryProductCatalougeDropdownAPI("BREEDSIZE");
  const { searchResults: variationThemeItems, execute: fetchVariationTheme } =
    useInventoryProductCatalougeDropdownAPI("VARIATION_THEME");
  const { execute: createDropdownItemsAPI } = useInventoryProductCatalogueDropdownItemCreateAPI();
  const fetchCountryAPI = useInventoryCountryListAPI();
  const updateProductCatalogueAPI = useInventoryProductCatalogueUpdateAPI();
  const { execute: productDetailsAPI, productDetails } = useInventoryProductDetailsAPI();
  const fetchUnitListAPI = usePOUnitAPI();
  const fetchManufacturerListAPI = useInventoryManufacturereListActiveAPI();
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const breedSizeItemsList = useSelectMenuReducer(breedSizeItems, "breed_size", "breed_size_id");
  const variationThemeItemsList = useSelectMenuReducer(variationThemeItems, "variation_theme", "variation_theme_id");
  const flavourItemsList = useSelectMenuReducer(flavourItems, "name", "flavour_id");
  const unitOptions = useSelectMenuReducer(unitList, "name", "id");
  const categoryListItem = useSelectMenuReducer(categoryItems, "category_name", "category_id");
  const subCategoryListItem = useSelectMenuReducer(subCategoryItems, "sub_category_name", "sub_category_id");
  const subSubCategoryListItem = useSelectMenuReducer(
    subSubcategoryItems,
    "sub_subcategory_name",
    "sub_subcategory_id",
  );
  const originList = useSelectMenuReducer(countryList, "name", "id");
  const { execute: fetchPackEligibility } = usePackSellEligibilityAPI();
  const { setVisible, setInvisible } = useModalActions("PRODUCT_CATALOGUE_ONBOARD_IMAGES_MODAL");
  const manufacturerItemsList = useSelectMenuReducer(manufacturerList, "manufacturer_name", "manufacturer_id");
  const breedList = useSelectMenuReducer(breedSpecificList, "pet_breed", "id");
  const { execute: uploadToS3 } = useFileToS3LinkGeneratorAPI();
  const { execute: fetchBreed } = useBreedListAPI();
  const { execute: fetchCouponSubCategory, serviceSubCategory: couponSubCategory } = useCouponSubCategoryAPI();
  const animalType = formHook.watch("animalType");
  const { control, watch, setValue } = formHook;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "packs",
  });
  const productId = params?.get("product_id");
  const isEditMode = !!productId;
  const [deletedImageIds, setDeletedImageIds] = useState<number[]>([]);
  const [existingPackCount, setExistingPackCount] = useState(0);
  const [editableIndex, setEditableIndex] = useState<number | null>(null);
  const [_tempImages, setTempImages] = useState<any[]>([]);

  const imagesFieldFormHook = useFieldArray<IInventoryFormFields>({
    name: "image",
    control: formHook.control,
  });

  const handleUploadAndSetImages = async () => {
    const existingImages = formHook.getValues("image") || [];
    const files = formHook.getValues("catalogue_images_files");
    if (!files?.length) return;
    const uploadedImages: IInventoryCatalogueImage[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const response = await uploadToS3(file, productDetails?.vetic_variant_id, "product_setup_catalogue");
        if (response?.status && response?.message) {
          uploadedImages.push({
            id: crypto.randomUUID(),
            image_url: response.message,
            is_thumbnail: false,
            is_front: false,
            is_back: false,
          });
        } else {
          toast.error(`Failed to upload ${file.name}. Please try again.`);
        }
      } catch (error) {
        console.error("Upload failed", error);
        toast.error(`Something went wrong while uploading ${file.name}`);
      }
    }
    const allImages = [...existingImages, ...uploadedImages];
    formHook.setValue("image", allImages, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  useEffect(() => {
    if (formHook.getValues("catalogue_images_files")?.length > 0) {
      formHook.trigger("catalogue_images_files").then(async (isValid) => {
        if (isValid) {
          await handleUploadAndSetImages();
          formHook.resetField("catalogue_images_files");
        }
      });
    }
  }, [formHook.watch("catalogue_images_files")]);

  useEffect(() => {
    const productId = params.get("product_id");
    if (productId) {
      productDetailsAPI(productId);
    }
  }, [params.get("product_id")]);

  const getThumbnailImage = () => {
    const thumbnail = imagesFieldFormHook?.fields?.filter((_field, index) => {
      return formHook.watch(`image.${index}.is_thumbnail` as const);
    });
    return thumbnail?.length > 0 ? thumbnail[0]?.id : null;
  };

  const getFrontImage = () => {
    const frontImage = imagesFieldFormHook?.fields?.filter((_field, index) => {
      return formHook.watch(`image.${index}.is_front` as const);
    });
    return frontImage?.length > 0 ? frontImage[0]?.id : null;
  };

  const getBackImage = () => {
    const backImage = imagesFieldFormHook?.fields?.filter((_field, index) => {
      return formHook.watch(`image.${index}.is_back` as const);
    });
    return backImage?.length > 0 ? backImage[0]?.id : null;
  };

  const formattedTags = (productDetails?.product_catalogue?.medicine_tags || productDetails?.tags || []).map((tag) => ({
    label: tag,
    value: tag,
  }));

  const customStyles = {
    placeholder: (provided) => ({
      ...provided,
      "@apply text-sm": "text-xs",
    }),
    singleValue: (provided) => ({
      ...provided,
      "@apply text-sm": "",
    }),
    multiValue: (provided) => ({
      ...provided,
      "@apply text-sm": "text-xs",
    }),
    input: (provided) => ({
      ...provided,
      "@apply text-sm": "",
    }),
  };

  useEffect(() => {
    const catalogue = productDetails?.product_catalogue;
    if (!catalogue) return;
    formHook.setValue("appLevelDiscount", catalogue?.app_discount_percentage);
    formHook.setValue("breadSize", catalogue?.breed_size);
    formHook.setValue("breadSpecific", catalogue?.breed_specific);
    formHook.setValue("category", catalogue?.category?.category_name);
    formHook.setValue("categoryId", catalogue?.category?.category_id);
    formHook.setValue("flavour", catalogue?.flavour);
    formHook.setValue("productDescription", catalogue?.product_description);
    formHook.setValue("productForm", catalogue?.form?.toLowerCase() || "");
    formHook.setValue("itemName", catalogue?.product_display_name);
    formHook.setValue("productWeight", catalogue?.weight);
    formHook.setValue("subCategoryId", catalogue?.sub_category?.sub_category_id);
    formHook.setValue("subCategory", catalogue?.sub_category?.sub_category_name);
    formHook.setValue("subSubCategory", catalogue?.sub_subcategory?.sub_subcategory_name);
    formHook.setValue("subSubCategoryId", catalogue?.sub_subcategory?.sub_subcategory_id);
    formHook.setValue("productHeight", catalogue?.dimensions?.height);
    formHook.setValue("productLength", catalogue?.dimensions?.length);
    formHook.setValue("productWidth", catalogue?.dimensions?.width);
    formHook.setValue("unitCount", catalogue?.unit_count);
    formHook.setValue("importerContactInformation", catalogue?.importer_contact_information);
    formHook.setValue("variationTheme", catalogue?.variation_theme);
    formHook.setValue("external_product", catalogue?.external_product_information);
    formHook.setValue("countryOrigin", catalogue?.country_of_origin);
    formHook.setValue("packageWeight", catalogue?.package_weight);
    const packChildren = (productDetails?.product_catalogue?.pack_sell_children ?? []) as {
      child_variant_pack_size: number;
      discount: number;
      is_highlight_icon: boolean;
      visible: boolean;
    }[];
    if (packChildren?.length > 0) {
      remove();
      packChildren.forEach((pack) => {
        append({
          pack: pack?.child_variant_pack_size,
          discount: pack?.discount,
          highlight: pack?.is_highlight_icon,
          visible: pack?.visible,
        });
      });
      setExistingPackCount(packChildren.length);
      formHook.setValue("packsNumber", true);
    } else {
      setExistingPackCount(0);
    }
    const ageRange = catalogue?.age_range?.toLowerCase();
    const validAgeRanges = ["puppy", "adult", "kitten", "cat"];
    formHook.setValue("age_range", validAgeRanges.includes(ageRange) ? ageRange : "");
    formHook.setValue("image", catalogue?.image || [], {
      shouldValidate: true,
      shouldDirty: true,
    });
    const capitalize = (str: string) => (str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "");
    formHook.setValue("unitType", capitalize(catalogue?.unit_type));
    formHook.setValue("appVisible", catalogue?.variant_on_app);
    formHook.setValue("manufacturer", catalogue?.manufacturer?.manufacturer_name);
    formHook.setValue("manufacturerId", catalogue?.manufacturer?.manufacturer_id);
    formHook.setValue("manufacturerContactInformation", catalogue?.manufacturer_contact_information);
    if (catalogue?.medicine_tags?.length > 0) {
      formHook.setValue("medicine_tags", catalogue?.medicine_tags);
    } else {
      formHook.setValue("medicine_tags", []);
    }
    formHook.setValue("composition", catalogue?.composition || "");
    const hasQCData =
      catalogue?.product_display_name ||
      catalogue?.weight ||
      catalogue?.unit_count ||
      catalogue?.pack_sell_children?.length > 0 ||
      catalogue?.dimensions?.height ||
      catalogue?.dimensions?.length ||
      catalogue?.dimensions?.width;
    formHook.setValue("qc_catalogue", !!hasQCData);
  }, [productDetails]);

  useEffect(() => {
    if (!productCopyDetails?.product_catalogue) return;
    if (productCopyDetails?.product_catalogue) {
      formHook.setValue("appLevelDiscount", productCopyDetails?.product_catalogue?.app_discount_percentage);
      formHook.setValue("breadSize", productCopyDetails?.product_catalogue?.breed_size);
      formHook.setValue("category", productCopyDetails?.product_catalogue?.category?.category_name);
      formHook.setValue("categoryId", productCopyDetails?.product_catalogue?.category?.category_id);
      formHook.setValue("flavour", productCopyDetails?.product_catalogue?.flavour);
      formHook.setValue("productDescription", productCopyDetails?.product_catalogue?.product_description);
      formHook.setValue("productForm", productCopyDetails?.product_catalogue?.form);
      formHook.setValue("itemName", productCopyDetails?.product_catalogue?.product_display_name);
      formHook.setValue("productWeight", productCopyDetails?.product_catalogue?.weight);
      formHook.setValue("subCategoryId", productCopyDetails?.product_catalogue?.sub_category?.sub_category_id);
      formHook.setValue("subCategory", productCopyDetails?.product_catalogue?.sub_category?.sub_category_name);
      formHook.setValue("subSubCategory", productCopyDetails?.product_catalogue?.sub_subcategory?.sub_subcategory_name);
      formHook.setValue("subSubCategoryId", productCopyDetails?.product_catalogue?.sub_subcategory?.sub_subcategory_id);
      formHook.setValue("manufacturer", productCopyDetails?.product_catalogue?.manufacturer?.manufacturer_name);
      formHook.setValue("manufacturerId", productCopyDetails?.product_catalogue?.manufacturer?.manufacturer_id);
      const capitalize = (str) => {
        if (!str) return "";
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
      };
      const productForm = productCopyDetails?.product_catalogue?.form?.toLowerCase() === "dry" ? "dry" : "wet";
      formHook.setValue("productForm", productForm);
      const breedsSpecific = productCopyDetails?.product_catalogue?.breed_specific;
      formHook.setValue("breadSpecific", breedsSpecific);
      const ageRange = productCopyDetails?.product_catalogue?.age_range?.toLowerCase();
      const validAgeRanges = ["puppy", "adult", "kitten", "cat"];
      const selectedAgeRange = validAgeRanges.includes(ageRange) ? ageRange : "";
      formHook.setValue("age_range", selectedAgeRange);
      const unitType = capitalize(productCopyDetails?.product_catalogue?.unit_type);
      formHook.setValue("unitType", unitType);
      formHook.setValue("appVisible", productCopyDetails?.product_catalogue?.variant_on_app);
    }
  }, [productCopyDetails]);

  useEffect(() => {
    fetchCountryAPI();
    fetchBreedSize();
    fetchFlavour();
    fetchCategory();
    fetchSubCategory();
    fetchSubsubCategory();
    fetchUnitListAPI();
    fetchVariationTheme();
    fetchManufacturerListAPI();
  }, []);

  useEffect(() => {
    const vmscategoryId = formHook.getValues("vmscategoryId");
    if (vmscategoryId && vmscategoryId !== "0") {
      fetchCouponSubCategory(vmscategoryId);
    }
  }, [formHook.watch("vmscategoryId")]);

  const showComposition = couponSubCategory?.some(
    (cat: any) => cat?.subcategory_id === formHook.watch("vmsSubcategoryId") && cat?.is_medicine,
  );

  useEffect(() => {
    if (animalType) {
      let animalId = "";
      if (animalType.toLowerCase() === "dog") animalId = "1";
      else if (animalType.toLowerCase() === "cat") animalId = "2";
      fetchBreed(animalId);
    }
  }, [animalType]);

  const validateConditionalFormFields = (data: IInventoryProductCatalogueFormFields) => {
    let isValid = false;
    if (
      data?.appLevelDiscount !== null ||
      data?.itemName !== "" ||
      data?.productWeight !== null ||
      data?.category !== "" ||
      data?.manufacturer !== ""
    ) {
      isValid = true;
    }
    return isValid;
  };

  const onSubmit = (data: IInventoryProductCatalogueFormFields) => {
    const isFormSubmissionValid = validateConditionalFormFields(data);
    if (!isFormSubmissionValid) {
      return;
    }
    if (data && isFormSubmissionValid) {
      setLoader(true);
      const constructedBody: IInventoryCataloguePropsBody & {
        medicine_tags: string[] | null;
        composition: string | null; } = {
        action: "UPDATE_PRODUCT_CATALOGUE",
        age_range: data?.age_range || null,
        app_discount_percentage: parseFloat(`${data?.appLevelDiscount}`),
        breed_size: data.breadSize === "" ? null : data.breadSize,
        breed_specific: data.breadSpecific || null,
        category: {
          category_id: data.category === "" ? null : data.categoryId,
          category_name: data.category === "" ? null : data.category,
        },
        sub_category: {
          sub_category_id: data.subCategory === "" ? null : data.subCategoryId,
          sub_category_name: data.subCategory === "" ? null : data.subCategory,
        },
        sub_subcategory: {
          sub_subcategory_id: data.subSubCategory === "" ? null : data.subSubCategoryId,
          sub_subcategory_name: data.subSubCategory === "" ? null : data.subSubCategory,
        },
        dimensions: {
          height: parseFloat(`${data?.productHeight}`),
          length: parseFloat(`${data.productLength}`),
          width: parseFloat(`${data.productWidth}`),
        },
        flavour: data.flavour || null,
        form: data.productForm || null,
        manufacturer: {
          manufacturer_name: data.manufacturer === "" ? null : data.manufacturer,
          manufacturer_id: data.manufacturer === "" ? null : data.manufacturerId,
        },
        product_description: data.productDescription === "" ? null : data.productDescription,
        product_display_name: data.itemName,
        unit_type: data.unitType || null,
        variant_on_app: data?.appVisible ? true : false,
        weight: parseFloat(`${data.productWeight}`),
        updated_at: Math.floor(Date.now() / 1000),
        image: data.image,
        manufacturer_contact_information: data?.manufacturerContactInformation || null,
        importer_contact_information: data?.importerContactInformation || null,
        country_of_origin: data.countryOrigin || null,
        external_product_information: data?.external_product || null,
        unit_count: parseFloat(`${data?.unitCount}`),
        variation_theme: data?.variationTheme || null,
        package_weight: parseFloat(`${data.packageWeight}`),
        pack_sell_children: data.packsNumber
          ? data.packs.map((pack) => ({
              child_variant_pack_size: parseInt(`${pack.pack}`),
              discount: parseInt(`${pack.discount}`),
              is_highlight_icon: pack.highlight,
              visible: pack.visible,
            }))
          : [],
        medicine_tags: data?.medicine_tags?.length === 0 ? null : data?.medicine_tags,
        composition: (showComposition && (data?.composition?.trim() || (isEditMode && productDetails?.general_details?.salt_name?.trim()))) || null,
      };
      updateProductCatalogueAPI(constructedBody, productDetails?.vetic_variant_id)
        .then(({ status }) => {
          setLoader(false);
          if (status) {
            navigate(`/app/inventory/product-list`);
          }
        })
        .catch(() => {
          setLoader(false);
        });
    }
  };

  const handelCreateItems = (item: string, CATALOGUE) => {
    let constructedBody: IInventoryCreateDropdownListProps = null;
    if (CATALOGUE === "FLAVOUR") {
      constructedBody = {
        flavour_name: item,
      };
    } else if (CATALOGUE === "BRAND") {
      constructedBody = {
        flavour_name: item,
      };
    } else if (CATALOGUE === "FORM") {
      constructedBody = {
        form_name: item,
      };
    } else if (CATALOGUE === "BREEDSIZE") {
      constructedBody = {
        breed_size: item,
      };
    } else if (CATALOGUE === "AGERANGE") {
      constructedBody = {
        age_range: item,
      };
    } else if (CATALOGUE === "VARIATION_THEME") {
      constructedBody = {
        variation_theme: item,
      };
    }
    createDropdownItemsAPI(constructedBody, CATALOGUE).then(({ status }) => {
      if (status) {
        if (CATALOGUE === "FLAVOUR") {
          fetchFlavour().then(() => {
            formHook.setValue("flavour", item);
          });
        } else if (CATALOGUE === "BREEDSIZE") {
          fetchBreedSize().then(() => {
            formHook.setValue("breadSize", item);
          });
        } else if (CATALOGUE === "VARIATION_THEME") {
          fetchVariationTheme().then(() => {
            formHook.setValue("variationTheme", item);
          });
        }
      }
    });
  };

  const handleOpenCopyModal = () => {
    setCopyModal(true);
  };

  useEffect(() => {
    if (imagesFieldFormHook.fields) {
      setTempImages([...imagesFieldFormHook.fields]);
    }
  }, [imagesFieldFormHook.fields]);

  useEffect(() => {
    if (isEditMode) return;
    const qcEnabled = formHook.watch("qc_catalogue");
    if (qcEnabled && productDetails?.general_details?.sku_name) {
      formHook.setValue("itemName", productDetails.general_details.sku_name, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [formHook.watch("qc_catalogue"), productDetails]);

  const isMedicine =
    !!watch("is_medicine") ||
    !!productDetails?.general_details?.is_medicine ||
    !!productDetails?.product_catalogue?.medicine_tags?.length;

  return (
    <div>
      <div className="flex flex-col gap-6 sm:flex-row sm:gap-16 sm:items-center bg-white p-6 rounded-md">
        <div>
          <h2 className="font-medium text-sm">QC Catalogue</h2>
          <p className="mt-1 max-w-4xl text-xs text-[#6e7280]">SKU Details, UPC codes and lot number management</p>
        </div>
        <div className={!productDetails ? "opacity-50 cursor-not-allowed pointer-events-none" : ""}>
          <ToggleWithIcon
            toggleState={formHook.watch("qc_catalogue")}
            onToggleChange={() => {
              if (!productDetails) return;
              formHook.setValue("qc_catalogue", !formHook.watch("qc_catalogue"), { shouldDirty: true });
            }}
          />
        </div>
      </div>
      {formHook.watch("qc_catalogue") && (
        <div className="bg-white p-6 rounded-md">
          <h2 className="font-medium text-sm">QC Catalogue</h2>
          <form className="mt-2 w-full" onSubmit={formHook.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-5 mt-4">
              <div>
                <div>
                  <p>
                    Item Display Name <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="mt-2 flex flex-col gap-4 sm:flex-row sm:gap-5">
                  <Input
                    className="w-full sm:w-2/6 text-xs"
                    placeholder="e.g. Royal Canin Perian Adult"
                    register={formHook.register("itemName", {
                      required: true,
                      ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                    })}
                    fieldError={formHook.formState.errors.itemName}
                    errorMessages={[
                      forAllAcceptWithOutStartWithDotAndSpace.errors,
                      { message: "Item Name is required", type: "required" },
                    ]}
                  />
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                  >
                    <button
                      className="flex items-center text-blue-500 text-xs whitespace-nowrap"
                      onClick={(e) => {
                        e.preventDefault();
                        handleOpenCopyModal();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                        }
                      }}
                    >
                      <span>
                        <IoMdArrowRoundDown className="w-4 h-4" />
                      </span>
                      Copy from other Product
                    </button>
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div className="">
                    <p>
                      Category 1 <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Category 1 is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("category", item.title);
                          formHook.setValue("categoryId", item.id);
                        }
                      }}
                      fieldError={formHook.formState.errors.category}
                      register={formHook.register("category", { required: true })}
                      selectItems={categoryListItem}
                      placeholder="e.g. Dog"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      defaultSelected={categoryListItem?.filter((oc) => oc.title === formHook.watch("category"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="w-full">
                    <p>
                      Category 2 <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Category 2 is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("subCategory", item.title);
                          formHook.setValue("subCategoryId", item.id);
                        }
                      }}
                      fieldError={formHook.formState.errors.subCategory}
                      register={formHook.register("subCategory", { required: true })}
                      selectItems={subCategoryListItem}
                      placeholder="e.g. Dog Food"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      defaultSelected={
                        subCategoryListItem?.filter((oc) => oc.title === formHook.watch("subCategory"))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="w-full">
                    <p>
                      Category 3 <span className="text-red-400">*</span>
                    </p>
                  </div>
                  <div className="w-full mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Category 3 is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("subSubCategory", item.title);
                          formHook.setValue("subSubCategoryId", item.id);
                        }
                      }}
                      fieldError={formHook.formState.errors.subSubCategory}
                      register={formHook.register("subSubCategory", { required: true })}
                      selectItems={subSubCategoryListItem}
                      placeholder="e.g. Dog Wet Food"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      defaultSelected={
                        subSubCategoryListItem?.filter((oc) => oc.title === formHook.watch("subSubCategory"))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5">
                <div className="flex flex-col w-full sm:w-2/6">
                  <p>Item Tags</p>
                  <div className="w-full mt-2">
                    <div className="border rounded-lg h-auto">
                      <CreatableSelect
                        isMulti
                        value={(formHook.watch("medicine_tags") || []).map((t: string) => ({ label: t, value: t }))}
                        onChange={(newValue) => {
                          const cleaned = (newValue || [])
                            .map((opt: any) => opt?.value?.trim())
                            .filter((val: string) => val && val.length > 0);
                          formHook.setValue("medicine_tags", cleaned);
                        }}
                        className={`text-sm ${formattedTags?.length > 6 && "h-40 overflow-scroll"} border-none`}
                        options={[]}
                        styles={customStyles}
                      />
                    </div>
                  </div>
                </div>
                {showComposition && (
                  <div className="flex flex-col w-full sm:w-2/6">
                    <div className="flex gap-2 items-center">
                      <p>Composition <span className="text-red-400">*</span></p>
                      <div className="text-[#6e7280] bg-[#f5f5f5] rounded-md py-1 px-3 text-xs font-medium">
                        Medicine Only
                      </div>
                    </div>
                    <div className="w-full mt-2">
                      <Input
                        className="text-xs"
                        placeholder="e.g. Paracetamol 500mg"
                        register={formHook.register("composition", {
                          required: true,
                          ...forAllAcceptWithOutStartWithDotAndSpace.validations,
                        })}
                        fieldError={formHook.formState.errors?.composition}
                        errorMessages={[
                          forAllAcceptWithOutStartWithDotAndSpace.errors,
                          { message: "Composition is required", type: "required" },
                        ]}
                      />
                    </div>
                  </div>
                )}
              </div>

              {copyModal && <CopyFromOtherProduct copyModal={copyModal} setCopyModal={setCopyModal} />}
              <div>
                <div>
                  <p>
                    Product Description <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="w-full mt-2">
                  <textarea
                    required
                    value={formHook.watch("productDescription")}
                    className="border-gray-300 border-greay-500 border rounded-md w-full h-32 text-xs"
                    placeholder="e.g. Write about the product, ingredients, expiry date."
                    onChange={(e) => formHook.setValue("productDescription", e.target.value)}
                  />
                </div>
              </div>
              <div className="flex-1">
                <div className="w-full">
                  <p>
                    Images <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="flex w-full mt-2">
                  <div className="w-full sm:w-2/6">
                    <InputWithFileUpload
                      variant="DRAG_AND_DROP"
                      fieldError={formHook.formState.errors?.catalogue_images_files ?? {}}
                      register={formHook?.register("catalogue_images_files", {
                        required: false,
                        validate: {
                          maxSize: (files: FileList) => {
                            return (
                              Array.from(files).every((file) => file.size < 5 * 1024 * 1024) ||
                              "Each file must be smaller than 5MB"
                            );
                          },
                          fileType: (files: FileList) => {
                            const validTypes = ["image/png", "image/jpeg"];
                            return (
                              Array.from(files).every((file) => validTypes.includes(file.type)) || "Invalid file type"
                            );
                          },
                        },
                        onChange: () => {
                          setVisible();
                        },
                      })}
                      type="file"
                      onClear={() => {
                        formHook.resetField("catalogue_images_files");
                      }}
                      className="w-full"
                      isMultiple
                      id={"catalogue_images_files"}
                      errorMessages={[{ type: "required", message: "Image attachment is required." }]}
                      dragAndDropDescription={{ allowedFormats: ["JPEG", "PNG"], maxFileSize: "5MB" }}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    {imagesFieldFormHook.fields?.slice(0, 2).map((file: any, index) => (
                      <div key={file.id} className="flex items-center gap-8 px-4 py-2">
                        <img
                          src={file?.image_url}
                          alt={`uploaded-img-${index}`}
                          className="rounded-md w-40 h-44 object-cover"
                        />
                      </div>
                    ))}
                    {imagesFieldFormHook.fields?.length > 2 ? (
                      <button
                        className="bg-gray-300 px-4 p-1 rounded-2xl text-black text-xs"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setVisible();
                        }}
                      >
                        {" "}
                        + {imagesFieldFormHook.fields?.length - 2}
                      </button>
                    ) : (
                      imagesFieldFormHook.fields?.length > 0 && (
                        <button
                          className="bg-gray-300 px-4 p-1 rounded-2xl text-black text-xs"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setVisible();
                          }}
                        >
                          View Images
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <div className="w-full">
                  <p>
                    Dimensions <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="flex gap-2 w-full mt-2">
                  <Input
                    className="text-xs"
                    placeholder="Enter Length"
                    register={formHook.register("productLength", {
                      required: true,
                      ...forNumericWithDecimal.validations,
                    })}
                    endIcon={<span className="font-bold">CM</span>}
                    fieldError={formHook.formState.errors.productLength}
                    errorMessages={[forNumericWithDecimal.errors, { message: "Length is required", type: "required" }]}
                  />
                  <Input
                    endIcon={<span className="font-bold">CM</span>}
                    className="text-xs"
                    placeholder="Enter Width"
                    register={formHook.register("productWidth", {
                      required: true,
                      ...forNumericWithDecimal.validations,
                    })}
                    fieldError={formHook.formState.errors.productWidth}
                    errorMessages={[forNumericWithDecimal.errors, { message: "Width is required", type: "required" }]}
                  />
                  <Input
                    endIcon={<span className="font-bold">CM</span>}
                    className="text-xs"
                    placeholder="Enter Height"
                    register={formHook.register("productHeight", {
                      required: true,
                      ...forNumericWithDecimal.validations,
                    })}
                    fieldError={formHook.formState.errors.productHeight}
                    errorMessages={[forNumericWithDecimal.errors, { message: "Height is required", type: "required" }]}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div>
                    <p>Breed Specific</p>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      placeholder="Breed"
                      showTooltips={true}
                      showDropdownIcon={true}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("breadSpecific", item.title);
                        }
                      }}
                      selectItems={breedList}
                      defaultSelected={breedList?.filter((oc) => oc.title === formHook.watch("breadSpecific"))[0]}
                      errorMessages={[{ type: "required", message: "" }]}
                      fieldError={formHook.formState.errors.breadSpecific}
                      register={formHook.register("breadSpecific", { required: false })}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div>
                    <p>
                      Breed Size <span className="text-red-400">*</span>{" "}
                    </p>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Breed Size is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("breadSize", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.breadSize}
                      register={formHook.register("breadSize", { required: true })}
                      selectItems={breedSizeItemsList}
                      placeholder="Select Breed Size"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      addAction={{
                        title: `+ ${"Add"}`,
                        callBack: (item: string) => handelCreateItems(item, "BREEDSIZE"),
                      }}
                      defaultSelected={breedSizeItemsList?.filter((oc) => oc.title === formHook.watch("breadSize"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div>
                    <p>Unit Count</p>
                  </div>
                  <div className="mt-2">
                    <Input
                      className="text-xs"
                      placeholder="e.g. 30"
                      register={formHook.register("unitCount", {
                        required: false,
                        ...forOnlyNumber.validations,
                      })}
                      fieldError={formHook.formState.errors?.unitCount}
                      errorMessages={[forOnlyNumber.errors, { message: "", type: "required" }]}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div>
                    <p>Unit Count Type</p>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("unitType", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.unitType}
                      register={formHook.register("unitType", { required: false })}
                      selectItems={unitOptions}
                      placeholder="Select Unit Count Type"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      defaultSelected={unitOptions?.filter((oc) => oc.title === formHook.watch("unitType"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div className="w-full">
                    <p>Item Form</p>
                  </div>
                  <div className="mt-2">
                    <ToggleableRadioGroup
                      value={formHook.watch("productForm")}
                      onChange={(val) => formHook.setValue("productForm", val)}
                      options={[
                        { id: "dry", title: "Dry Form", colorClassName: "bg-green-500" },
                        { id: "wet", title: "Wet Form", colorClassName: "bg-green-500" },
                      ]}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="w-full">
                    <p>Age Range Desc</p>
                  </div>
                  <div className="mt-2">
                    <ToggleableRadioGroup
                      value={formHook.watch("age_range")}
                      onChange={(val) => formHook.setValue("age_range", val)}
                      options={[
                        { id: "puppy", title: "Puppy", colorClassName: "bg-green-500" },
                        { id: "adult", title: "Adult Dog", colorClassName: "bg-green-500" },
                        { id: "kitten", title: "Kitten", colorClassName: "bg-green-500" },
                        { id: "cat", title: "Cat", colorClassName: "bg-green-500" },
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div>
                    <p>Variation Theme</p>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Variation Theme is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("variationTheme", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.variationTheme}
                      register={formHook.register("variationTheme", { required: false })}
                      selectItems={variationThemeItemsList}
                      placeholder="Select Variation Theme"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      addAction={{
                        title: `+ ${"Add"}`,
                        callBack: (item: string) => handelCreateItems(item, "VARIATION_THEME"),
                      }}
                      defaultSelected={
                        variationThemeItemsList?.filter((oc) => oc.title === formHook.watch("variationTheme"))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex gap-3 items-center">
                    <p>Flavour Name</p>
                    <div className="bg-[#eeeeee] text-[#6d7181] rounded-md py-1 px-3 text-xs">Food Items Only</div>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("flavour", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.flavour}
                      register={formHook.register("flavour", { required: false })}
                      selectItems={flavourItemsList}
                      placeholder="Select Flavour Name"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      addAction={{
                        title: `+ ${"Add"}`,
                        callBack: (item: string) => handelCreateItems(item, "FLAVOUR"),
                      }}
                      defaultSelected={flavourItemsList?.filter((oc) => oc.title === formHook.watch("flavour"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <p>External Product Info</p>
                </div>
                <div className="w-full mt-2">
                  <textarea
                    value={formHook.watch("external_product")}
                    className="border-gray-300 border-greay-500 border rounded-md w-full h-32 text-xs"
                    placeholder="e.g. Any vendor information"
                    onChange={(e) => formHook.setValue("external_product", e.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div className="w-full sm:w-2/12">
                    <p>Manufacturer</p>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "Manufacturer is required", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("manufacturer", item.title);
                          formHook.setValue("manufacturerId", item.id);
                        }
                      }}
                      fieldError={formHook.formState.errors.manufacturer}
                      register={formHook.register("manufacturer", { required: false })}
                      selectItems={manufacturerItemsList}
                      placeholder="Select Manufacturer"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      defaultSelected={
                        manufacturerItemsList?.filter((oc) => oc.title === formHook.watch("manufacturer"))[0]
                      }
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div>
                    <p>Manufacturer Contact Information</p>
                  </div>
                  <div className="mt-2">
                    <Input
                      className="text-xs"
                      placeholder="e.g. 420, Jhumri talaiya, Delhi 110011"
                      register={formHook.register("manufacturerContactInformation", {
                        required: false,
                      })}
                      fieldError={formHook.formState.errors.manufacturerContactInformation}
                      errorMessages={[{ message: "Manufacturer Contact Information is required", type: "required" }]}
                      maxLength={10}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div>
                    <p>Country of Origin</p>
                  </div>
                  <div className="mt-2">
                    <SearchableSelectMenu
                      errorMessages={[{ message: "", type: "required" }]}
                      onSelectItem={(item) => {
                        if (item) {
                          formHook.setValue("countryOrigin", item.title);
                        }
                      }}
                      fieldError={formHook.formState.errors.countryOrigin}
                      register={formHook.register("countryOrigin", { required: false })}
                      selectItems={originList}
                      placeholder="Select Country of Origin"
                      showTooltips={true}
                      showTypedErrors={true}
                      showDropdownIcon={true}
                      withDropDownSearch
                      defaultSelected={originList?.filter((oc) => oc.title === formHook.watch("countryOrigin"))[0]}
                      listBoxClassName="w-full"
                      className="text-gray-800"
                      containerClassName="w-full"
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div>
                    <p>Importer Contact Information </p>
                  </div>
                  <div className="mt-2 w-full sm:w-2/6">
                    <Input
                      className="text-xs"
                      placeholder="Importer Contact Information"
                      register={formHook.register("importerContactInformation", {
                        required: false,
                      })}
                      fieldError={formHook.formState.errors.importerContactInformation}
                      errorMessages={[{ message: "Importer Contact Information is required", type: "required" }]}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div className="flex-1">
                  <div className="flex gap-3 items-center">
                    <p>
                      Item Weight<span className="text-red-400">*</span>
                    </p>
                    <div className="bg-[#eeeeee] text-[#6d7181] rounded-md py-1 px-3 text-xs">Grams</div>
                  </div>
                  <div className="mt-2">
                    <Input
                      className="text-xs"
                      placeholder="Enter Item Weight"
                      register={formHook.register("productWeight", {
                        required: true,
                        ...forNumericWithDecimal.validations,
                      })}
                      fieldError={formHook.formState.errors.productWeight}
                      errorMessages={[
                        forNumericWithDecimal.errors,
                        { message: "Item Weight is required", type: "required" },
                      ]}
                    />
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex gap-3 items-center">
                    <p>
                      Package Weight<span className="text-red-400">*</span>
                    </p>
                    <div className="bg-[#eeeeee] text-[#6d7181] rounded-md py-1 px-3 text-xs">Grams</div>
                  </div>
                  <div className="mt-2">
                    <Input
                      className="text-xs"
                      placeholder="Weight of the item"
                      register={formHook.register("packageWeight", {
                        required: true,
                        ...forNumericWithDecimal.validations,
                      })}
                      fieldError={formHook.formState.errors.packageWeight}
                      errorMessages={[
                        forNumericWithDecimal.errors,
                        { message: "Package Weight is required", type: "required" },
                      ]}
                    />
                  </div>
                </div>
              </div>
              <div>
                <div>
                  <p>
                    Discount <span className="text-red-400">*</span>
                  </p>
                </div>
                <div className="mt-2 w-full sm:w-2/6">
                  <Input
                    className="text-xs"
                    maxLength={5}
                    placeholder="Enter Discount"
                    register={formHook.register("appLevelDiscount", {
                      required: true,
                      ...forTaxRegex.validations,
                    })}
                    fieldError={formHook.formState.errors.appLevelDiscount}
                    errorMessages={[forTaxRegex.errors, { message: "Discount is required", type: "required" }]}
                  />
                </div>
              </div>
              <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                <div>
                  <p>Visible on APP</p>
                </div>
                <div className="mt-2">
                  <ToggleWithIcon
                    onToggleChange={(status) => {
                      formHook.setValue("appVisible", status);
                    }}
                    toggleState={formHook.watch("appVisible")}
                  />
                </div>
              </div>
              <div>
                <div className="flex flex-col gap-4 sm:flex-row sm:gap-5 sm:items-center">
                  <p>Add Number of Packs</p>
                  <div
                    className={`${
                      fields.some((_, idx) => watch(`packs.${idx}.pack`) !== "")
                        ? "opacity-50 cursor-not-allowed pointer-events-none"
                        : ""
                    }`}
                  >
                    <ToggleWithIcon
                      onToggleChange={async (status) => {
                        if (status) {
                          if (!formHook.getValues("parentTitleId")) {
                            toast.error("Please select a parent product first.");
                            formHook.setValue("packsNumber", false);
                            return;
                          }
                          try {
                            const res = await fetchPackEligibility(formHook.getValues("parentTitleId"));
                            if (res?.status && res?.data?.is_pack_sell_eligible) {
                              formHook.setValue("packsNumber", true);
                              formHook.setValue("is_pack_sell_eligible", true);
                              if (fields.length === 0) {
                                append({ pack: "", discount: null, highlight: false, visible: false });
                                setEditableIndex(0);
                              }
                            } else {
                              toast.error("This product is not eligible for Pack Sell.");
                              formHook.setValue("packsNumber", false);
                              formHook.setValue("is_pack_sell_eligible", false);
                            }
                          } catch (err) {
                            toast.error("Failed to check pack eligibility. Try again.");
                            formHook.setValue("packsNumber", false);
                          }
                        } else {
                          formHook.setValue("packsNumber", false);
                          remove();
                        }
                      }}
                      toggleState={formHook.watch("packsNumber")}
                    />
                  </div>
                </div>
                {formHook.watch("packsNumber") &&
                  fields.map((field, index) => {
                    const isLastRow = index === fields.length - 1;
                    const isExistingPack = index < existingPackCount;
                    const packValue = watch(`packs.${index}.pack`);
                    const isInputEnabled = !isExistingPack || packValue === "" || editableIndex === index;
                    const fieldError = formHook.formState.errors?.packs?.[index]?.pack;
                    return (
                      <div key={field.id} className="flex flex-col gap-6 sm:flex-row sm:gap-12 sm:items-center mt-2">
                        <div>
                          {index === 0 && (
                            <p>
                              Pack Size <span className="text-red-400">*</span>
                            </p>
                          )}
                          <div className="mt-1">
                            <Input
                              placeholder="Pack of"
                              className={`text-xs ${!isInputEnabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                              register={formHook.register(`packs.${index}.pack`, {
                                required: true,
                                ...forNumericWithDecimal.validations,
                                validate: (value) => {
                                  if (Number(value) <= 0) return "No of pack must be greater than 0";
                                  const packsArray = watch("packs").map((p) => Number(p.pack));
                                  if (packsArray.filter((v) => v === Number(value)).length > 1) {
                                    return "Same number of pack cannot be added";
                                  }
                                  return true;
                                },
                              })}
                              fieldError={fieldError}
                              errorMessages={[
                                forNumericWithDecimal.errors,
                                { message: "Pack of is required", type: "required" },
                                { message: "No of pack must be greater than 0", type: "validate" },
                                { message: "Same number of pack cannot be added", type: "validate" },
                              ]}
                              disabled={!isInputEnabled}
                            />
                          </div>
                        </div>
                        <div>
                          {index === 0 && (
                            <p>
                              Pack Discount (%) <span className="text-red-400">*</span>
                            </p>
                          )}
                          <div className="mt-1">
                            <Input
                              placeholder="Discount"
                              className={`text-xs ${!isInputEnabled ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                              register={formHook.register(`packs.${index}.discount`, {
                                required: true,
                                ...forNumericWithDecimal.validations,
                                // validate: (value) => {
                                //   if (Number(value) <= 0) return "No of pack must be greater than 0";
                                //   const packsArray = watch("packs").map((p) => Number(p.discount));
                                //   if (packsArray.filter((v) => v === Number(value)).length > 1) {
                                //     return "Same number of pack cannot be added";
                                //   }
                                //   return true;
                                // },
                              })}
                              fieldError={fieldError}
                              errorMessages={[
                                forNumericWithDecimal.errors,
                                { message: "Discount of is required", type: "required" },
                              ]}
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <p>Highlight</p>
                          <ToggleWithIcon
                            toggleState={watch(`packs.${index}.highlight`)}
                            onToggleChange={(status) => {
                              if (status) fields.forEach((_, i) => setValue(`packs.${i}.highlight`, false));
                              setValue(`packs.${index}.highlight`, status);
                            }}
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <p>Visible</p>
                          <ToggleWithIcon
                            toggleState={watch(`packs.${index}.visible`)}
                            onToggleChange={(status) => setValue(`packs.${index}.visible`, status)}
                          />
                        </div>
                        {isLastRow ? (
                          <div
                            className="cursor-pointer text-[#6d717f]"
                            onClick={() => {
                              const lastPackValue = fields.length > 0 ? watch(`packs.${fields.length - 1}.pack`) : "";
                              if (!lastPackValue) {
                                toast.error("Please fill the last row before adding a new one");
                                return;
                              }
                              if (fieldError) {
                                toast.error("Please resolve the error before adding a new row");
                                return;
                              }
                              append({ pack: "", discount: null, highlight: false, visible: false });
                              setEditableIndex(fields.length);
                            }}
                          >
                            <FiPlusCircle size={20} />
                          </div>
                        ) : (
                          !isExistingPack && (
                            <div className="text-[#6d717f] cursor-pointer" onClick={() => remove(index)}>
                              <RiDeleteBinLine size={22} />
                            </div>
                          )
                        )}
                      </div>
                    );
                  })}
              </div>

              <div className="sticky flex justify-end mt-4">
                <button
                  type="submit"
                  disabled={loader}
                  className={clsx(
                    PRIMARY_BUTTON,
                    "w-full sm:w-auto disabled:bg-gray-400 disabled:text-gray-200 disabled:cursor-not-allowed",
                  )}
                >
                  {loader ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader className="h-4 w-4 animate-spin" />
                      Saving...
                    </div>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
            <ModalPrompt
              modalId="PRODUCT_CATALOGUE_ONBOARD_IMAGES_MODAL"
              dialogClassName="w-[800px]"
              title={<span>Verify Reordering Of The Images Catalogue</span>}
              button={{
                primaryButtonText: "Save",
                secondaryButtonText: "Cancel",
                primaryCallback: () => {
                  formHook.trigger("image").then(async (isValid) => {
                    if (isValid) {
                      deletedImageIds.forEach((id) => {
                        const indexToRemove = imagesFieldFormHook.fields.findIndex((f) => f.id === String(id));
                        if (indexToRemove > -1) imagesFieldFormHook.remove(indexToRemove);
                      });
                      setInvisible();
                      setDeletedImageIds([]);
                    }
                  });
                },
                secondaryCallback: () => {
                  setInvisible();
                  setDeletedImageIds([]);
                },
                primaryButtonClassname: "bg-blue-900",
              }}
              content={
                <div className="flex flex-col gap-y-3 mt-8 w-full h-72 overflow-y-scroll">
                  {imagesFieldFormHook.fields?.map((file: any, index) => {
                    if (deletedImageIds.includes(file.id)) return null;
                    return (
                      <div
                        key={file.file_name}
                        className="border-gray-200 grid grid-cols-3 sm:grid-cols-9 border rounded-md w-full"
                      >
                        <div className="col-span-2 bg-white shadow-md rounded-md w-28 h-28 overflow-hidden">
                          <img src={file?.image_url} alt="base 64" className="w-full h-full" />
                        </div>
                        <div className="flex flex-col justify-center items-center col-span-2">
                          <div className="flex justify-start items-center gap-x-2">
                            <p className="text-xs">Is Thumbnail</p>
                            <input
                              type="checkbox"
                              disabled={getThumbnailImage() ? getThumbnailImage() !== file.id : false}
                              checked={!!formHook.watch(`image.${index}.is_thumbnail`)}
                              onChange={(e) =>
                                formHook.setValue(`image.${index}.is_thumbnail`, e.target.checked, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="flex flex-col justify-center items-center col-span-1">
                          <div className="flex justify-start items-center gap-x-2">
                            <p className="text-xs">Front</p>
                            <input
                              type="checkbox"
                              disabled={
                                formHook.watch(`image.${index}.is_back`) || getFrontImage()
                                  ? getFrontImage() !== file.id
                                  : false
                              }
                              checked={!!formHook.watch(`image.${index}.is_front`)}
                              onChange={(e) =>
                                formHook.setValue(`image.${index}.is_front`, e.target.checked, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="flex flex-col justify-center items-center col-span-1">
                          <div className="flex justify-start items-center gap-x-2">
                            <p className="text-xs">Back</p>
                            <input
                              type="checkbox"
                              disabled={
                                formHook.watch(`image.${index}.is_front`) || getBackImage()
                                  ? getBackImage() !== file.id
                                  : false
                              }
                              checked={!!formHook.watch(`image.${index}.is_back`)}
                              onChange={(e) =>
                                formHook.setValue(`image.${index}.is_back`, e.target.checked, {
                                  shouldDirty: true,
                                  shouldValidate: true,
                                })
                              }
                            />
                          </div>
                        </div>
                        <div className="flex flex-col justify-center items-center col-span-1">
                          <button
                            onClick={() => {
                              setDeletedImageIds((prev) => [...prev, file.id]);
                            }}
                          >
                            <TrashIcon className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                        <div className="flex flex-col justify-center items-center col-span-1">
                          <button
                            onClick={() => {
                              index > 0 && imagesFieldFormHook.swap(index, index - 1);
                            }}
                          >
                            <ChevronUpIcon
                              className={clsx(
                                "w-6 h-6",
                                index > 0 ? "text-blue-500" : "text-gray-500 cursor-not-allowed",
                              )}
                            />
                          </button>
                          <button
                            onClick={() => {
                              index < imagesFieldFormHook.fields.length - 1 &&
                                imagesFieldFormHook.swap(index, index + 1);
                            }}
                          >
                            <ChevronDownIcon
                              className={clsx(
                                "w-6 h-6",
                                index < imagesFieldFormHook.fields.length - 1
                                  ? "text-blue-500"
                                  : "text-gray-500 cursor-not-allowed",
                              )}
                            />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              }
            />
          </form>
        </div>
      )}
    </div>
  );
};

export default CatalogDetailsFormComponent;
